import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    TrashIcon,
    TagIcon,
    FolderIcon,
    Squares2X2Icon,
    XMarkIcon,
    ChevronRightIcon,
    ChevronDownIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const AdminCategories = () => {
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    // Toggle states for sections
    const [activeSection, setActiveSection] = useState('categories'); // categories, subcategories

    // Form visibility states
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [showAddSubcategory, setShowAddSubcategory] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
    const [subcategoryForm, setSubcategoryForm] = useState({ name: '', category_id: '', description: '' });

    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchSubcategories = async () => {
        try {
            const response = await axios.get(`${API}/subcategories`);
            setSubcategories(response.data);
        } catch (error) {
            console.error('Failed to fetch subcategories:', error);
        }
    };



    // Delete handlers
    const handleDeleteCategory = async (categoryId) => {
        const hasSubcategories = subcategories.some(sub => sub.category_id === categoryId);
        if (hasSubcategories) {
            toast.error("Delete subcategories first before deleting this category.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/categories/${categoryId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Category deleted successfully!');
            fetchCategories();
        } catch (error) {
            toast.error('Failed to delete category');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSubcategory = async (subcategoryId) => {
        if (!window.confirm("Are you sure you want to delete this subcategory?")) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/subcategories/${subcategoryId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Subcategory deleted successfully!');
            fetchSubcategories();
        } catch (error) {
            toast.error('Failed to delete subcategory');
        } finally {
            setLoading(false);
        }
    };



    // Add handlers
    const handleAddCategory = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/categories`, categoryForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Category added successfully!');
            setCategoryForm({ name: '', description: '' });
            setShowAddCategory(false);
            fetchCategories();
        } catch (error) {
            toast.error('Failed to add category');
        } finally {
            setLoading(false);
        }
    };

    const handleAddSubcategory = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/subcategories`, subcategoryForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Subcategory added successfully!');
            setSubcategoryForm({ name: '', category_id: '', description: '' });
            setShowAddSubcategory(false);
            fetchSubcategories();
        } catch (error) {
            toast.error('Failed to add subcategory');
        } finally {
            setLoading(false);
        }
    };



    const TabButton = ({ id, label, icon: Icon }) => (
        <button
            onClick={() => setActiveSection(id)}
            className={`flex items-center px-6 py-3 text-sm font-medium transition-all border-b-2 ${activeSection === id
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
        >
            <Icon className="h-4 w-4 mr-2" />
            {label}
        </button>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm overflow-hidden"
        >
            <div className="p-4 lg:p-8 pb-0">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Organization</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage categories and subcategories</p>
                    </div>
                </div>

                <div className="flex border-b border-gray-100 mb-8 overflow-x-auto custom-scrollbar">
                    <div className="flex min-w-full">
                        <TabButton id="categories" label="Categories" icon={FolderIcon} />
                        <TabButton id="subcategories" label="Subcategories" icon={TagIcon} />
                    </div>
                </div>
            </div>

            <div className="p-4 lg:p-8 pt-0">
                {/* Categories Section */}
                {activeSection === 'categories' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="flex justify-end mb-6">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setShowAddCategory(true)}
                                className="bg-gray-900 text-white px-4 py-2 rounded-xl hover:bg-black transition-all shadow-sm flex items-center text-sm font-medium"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Add Category
                            </motion.button>
                        </div>

                        <AnimatePresence>
                            {showAddCategory && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="overflow-hidden mb-8"
                                >
                                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">New Category</h3>
                                            <button onClick={() => setShowAddCategory(false)} className="text-gray-400 hover:text-gray-600">
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                        <form onSubmit={handleAddCategory} className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Name</label>
                                                <div className="relative">
                                                    <FolderIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                    <input
                                                        type="text"
                                                        required
                                                        value={categoryForm.name}
                                                        onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                        placeholder="Category Name"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Description</label>
                                                <textarea
                                                    value={categoryForm.description}
                                                    onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    placeholder="Description (optional)"
                                                    rows={2}
                                                />
                                            </div>
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddCategory(false)}
                                                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-black transition-colors text-sm font-medium disabled:opacity-50"
                                                >
                                                    {loading ? 'Saving...' : 'Save Category'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="overflow-x-auto custom-scrollbar rounded-2xl border border-gray-100 bg-white">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50/50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Description</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">Subcategories</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {categories.map((category, index) => (
                                        <motion.tr
                                            key={category.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="hover:bg-gray-50/80 transition-colors"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 text-gray-500">
                                                        <FolderIcon className="h-4 w-4" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900">{category.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                                {category.description || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    {subcategories.filter(sub => sub.category_id === category.id).length}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleDeleteCategory(category.id)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1.5 hover:bg-red-50 rounded-lg"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                )}

                {/* Subcategories Section */}
                {activeSection === 'subcategories' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="flex justify-end mb-6">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setShowAddSubcategory(true)}
                                className="bg-gray-900 text-white px-4 py-2 rounded-xl hover:bg-black transition-all shadow-sm flex items-center text-sm font-medium"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Add Subcategory
                            </motion.button>
                        </div>

                        <AnimatePresence>
                            {showAddSubcategory && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="overflow-hidden mb-8"
                                >
                                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">New Subcategory</h3>
                                            <button onClick={() => setShowAddSubcategory(false)} className="text-gray-400 hover:text-gray-600">
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                        <form onSubmit={handleAddSubcategory} className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Name</label>
                                                    <div className="relative">
                                                        <TagIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                        <input
                                                            type="text"
                                                            required
                                                            value={subcategoryForm.name}
                                                            onChange={e => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                                                            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                            placeholder="Subcategory Name"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Parent Category</label>
                                                    <div className="relative">
                                                        <FolderIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                        <select
                                                            required
                                                            value={subcategoryForm.category_id}
                                                            onChange={e => setSubcategoryForm({ ...subcategoryForm, category_id: e.target.value })}
                                                            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white appearance-none text-sm"
                                                        >
                                                            <option value="">Select Category</option>
                                                            {categories.map(category => (
                                                                <option key={category.id} value={category.id}>{category.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Description</label>
                                                <textarea
                                                    value={subcategoryForm.description}
                                                    onChange={e => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    placeholder="Description (optional)"
                                                    rows={2}
                                                />
                                            </div>
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddSubcategory(false)}
                                                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-black transition-colors text-sm font-medium disabled:opacity-50"
                                                >
                                                    {loading ? 'Saving...' : 'Save Subcategory'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="overflow-x-auto custom-scrollbar rounded-2xl border border-gray-100 bg-white">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50/50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Parent Category</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Description</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {subcategories.map((subcategory, index) => {
                                        const category = categories.find(cat => cat.id === subcategory.category_id);
                                        return (
                                            <motion.tr
                                                key={subcategory.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="hover:bg-gray-50/80 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 text-gray-500">
                                                            <TagIcon className="h-4 w-4" />
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-900">{subcategory.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {category?.name || 'Unknown'}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                                    {subcategory.description || '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleDeleteSubcategory(subcategory.id)}
                                                        className="text-gray-400 hover:text-red-600 transition-colors p-1.5 hover:bg-red-50 rounded-lg"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                )}


            </div>
        </motion.div>
    );
};

export default AdminCategories;
